import React from "react";
import { useFocusVisible, useMergedRefs } from "@fluentui/react-components";
import { useFocusRects } from "@fluentui/utilities";
import { AppPaneGroup } from "owa-app-pane-group";
import classNames from "owa-classnames";
import { getDensity } from "owa-fabric-theme";
import { observer } from "owa-mobx-react";
import { isShySuiteHeaderMode } from "owa-suite-header-store";
import { isCapabilityEnabled } from "owa-capabilities";
import { bleedThroughCapability } from "owa-capabilities-definitions/lib/bleedThroughCapability";
import { newHeaderCapability } from "owa-capabilities-definitions/lib/newHeaderCapability";
import { groupsReportAbuseCapability } from "owa-capabilities-definitions/lib/groupsReportAbuseCapability";
import { addinsDrawerPaneUnderRibbonCapability } from "owa-capabilities-definitions/lib/addinsDrawerPaneUnderRibbonCapability";
import type { AppPaneUnderlayViewState } from "owa-app-pane-underlay";
import { AppPaneUnderlay } from "owa-app-pane-underlay-view";
import { getActiveDrawerPaneId, useDrawerPaneAnchor } from "owa-drawer";
import ProjectionContext from "owa-popout-v2/lib/context/ProjectionContext";
import { OwaThemeProvider } from "owa-react-dom";
import { useState } from "owa-react";
import { StatusBar, StatusBarTargetContext } from "owa-status-bar";
import { getApplicationSettings } from "owa-application-settings";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";
import { getIsMainAppHeightOverflow, getMainAppMinHeight, } from "owa-layout/lib/selectors/getAppHeightSelectors";
import { LazyGroupsReportAbuseDialogWrapper } from "owa-groups-report-abuse";

import { appContainer, monarchAwareBackground, mainContainer, main, bleedThrough, addOverflowY, hideOverflowY, appContainerHiddenOverflow, appContainerClippedOverflow, win32OverlayBackground, } from "./App.scss";
import { isGlobalFeatureEnabled } from "owa-feature-flags";

export interface AppProps {
    renderHeader?: () => React.ReactElement<{}> | undefined;
    children?: React.ReactNode;
    renderSkipLinkControl?: () => React.ReactElement<{}> | undefined;
    underlay?: AppPaneUnderlayViewState;
}

const defaultExport: React.FunctionComponent<AppProps> = observer(function App({ renderHeader, children, renderSkipLinkControl, underlay, }: AppProps) {
    // We need to set the minHeight of the mainApp browser until the component is mounted, otherwise
    // the view is going to get pushed at the bottom of the scrollbar by the other UI elements
    // while they start appearing on the UI.
    const isMounted = React.useRef(false);
    
    React.useEffect(() => {
        isMounted.current = true;
        
        const virtualKeyboard = navigator?.virtualKeyboard;
        if (virtualKeyboard) {
            try {
                virtualKeyboard.overlaysContent = false;
            }
            catch { }
        }
    }, []);
    
    const isMainAppHeightOverflow = getIsMainAppHeightOverflow();
    const mainAppMinHeight = getMainAppMinHeight();
    
    // This is to avoid creating an inline style object
    const mainAppMinHeightStyle = React.useMemo(() => {
        return mainAppMinHeight && isMainAppHeightOverflow && isMounted.current
            ? { minHeight: mainAppMinHeight }
            : {};
    }, [mainAppMinHeight, isMainAppHeightOverflow, isMounted.current]);
    
    const rootRef = React.useRef<HTMLDivElement>(null);
    // If the root element's window is different than the global window, pass in
    // the root element ref to the function so we initialize the focus rects in popout windows.
    const isRootElementInPopoutWindow = window != rootRef.current?.ownerDocument.defaultView;
    useFocusRects(isRootElementInPopoutWindow ? rootRef : undefined);
    
    const useMonarchStyles = getApplicationSettings("PlatformType").isMonarch;
    const isBleedThroughEnabled = isCapabilityEnabled(bleedThroughCapability);
    const isNewHeaderEnabled = isCapabilityEnabled(newHeaderCapability);
    const isGroupsReportAbuseEnabled = isCapabilityEnabled(groupsReportAbuseCapability);
    const isWin32OverlayEnabled = isWin32StylingEnabled();
    
    // Initializing the polyfill for :focus-visible
    const targetDocument = isRootElementInPopoutWindow
        ? rootRef.current?.ownerDocument
        : window.document;
    const focusVisibleRef = useFocusVisible<HTMLDivElement>({ targetDocument });
    
    // Use ProjectionContext (same source as AddinsPane/AddinsResizableDrawer) so the
    // underlay and drawer always resolve the same window. Unlike a rootRef-based window,
    // this is correct on the first render and reactive when the projection's drawer opens.
    const projectionWindow = React.useContext(ProjectionContext);
    
    const shouldMountDrawerInAnchor = isCapabilityEnabled(addinsDrawerPaneUnderRibbonCapability);
    const { mountNode, orderStyle } = useDrawerPaneAnchor(shouldMountDrawerInAnchor ? getActiveDrawerPaneId(projectionWindow) : undefined);
    const statusBarTargetRef = React.useRef<HTMLDivElement | null>(null);
    const [isStatusBarReady, setIsStatusBarReady] = useState(false);
    const setStatusBarTarget = React.useCallback((target: HTMLDivElement | null) => {
        statusBarTargetRef.current = target;
        setIsStatusBarReady(target !== null);
    }, []);
    
    // Mount the underlay into the drawer's anchor (reading-pane/form canvas) when present.
    const underlayMountNode = mountNode ?? undefined;
    
    return (<StatusBarTargetContext.Provider value={isStatusBarReady ? statusBarTargetRef.current : null}>
            <OwaThemeProvider id={"appContainer"} className={classNames(appContainer, useMonarchStyles && monarchAwareBackground, isBleedThroughEnabled && bleedThrough, isWin32OverlayEnabled && win32OverlayBackground, isGlobalFeatureEnabled("cal-surface-outerscroll")
            ? appContainerClippedOverflow
            : appContainerHiddenOverflow)} partialTheme={getDensity()}>
                {renderSkipLinkControl?.()}
                {renderHeader?.()}
                <div className={classNames(mainContainer, "customScrollBar", isMainAppHeightOverflow ? addOverflowY : hideOverflowY)}>
                    <div 
    // `mainApp` id is used to handle dynamic layout scrollbars behavior and appearance
    id="mainApp" className={classNames(main, {
            isShyHeaderMode: isShySuiteHeaderMode(),
        })} style={mainAppMinHeightStyle} ref={useMergedRefs(rootRef, focusVisibleRef)}>
                        {children}
                        <AppPaneUnderlay underlay={underlay} mountNode={underlayMountNode} underlayStyle={orderStyle}/>
                        {isNewHeaderEnabled && <AppPaneGroup />}

                        {isGroupsReportAbuseEnabled && <LazyGroupsReportAbuseDialogWrapper />}
                    </div>
                </div>
                {projectionWindow === window && <StatusBar targetRef={setStatusBarTarget}/>}
            </OwaThemeProvider>
        </StatusBarTargetContext.Provider>);
}, "App");
export default defaultExport;
